import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import jsQR from 'jsqr';
import './QRScanner.css';

const QRScanner = () => {
  // State management
  const [scannedItems, setScannedItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [lastScannedQR, setLastScannedQR] = useState('');
  const [scanCooldown, setScanCooldown] = useState(false);
  
  // New states for meal selection
  const [selectedMealType, setSelectedMealType] = useState('lunch');
  const [todayMenu, setTodayMenu] = useState(null);
  const [menuLoading, setMenuLoading] = useState(true);
  
  // Refs
  const scannerInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanningIntervalRef = useRef(null);

  // Fetch today's menu on component mount
  useEffect(() => {
    fetchTodayMenu();
  }, []);
  
  // Focus input field for external scanner
  useEffect(() => {
    if (scannerInputRef.current && !isCameraActive) {
      scannerInputRef.current.focus();
    }
  }, [isCameraActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scanningIntervalRef.current) {
        clearInterval(scanningIntervalRef.current);
      }
    };
  }, []);

  // Fetch today's menu
  const fetchTodayMenu = async () => {
    setMenuLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/getTodayMenu`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      const data = await response.json();
      setTodayMenu(data);
      
      // Set default meal type based on available options
      if (data.hasMenu) {
        if (data.lunch.isActive && !data.dinner.isActive) {
          setSelectedMealType('lunch');
        } else if (data.dinner.isActive && !data.lunch.isActive) {
          setSelectedMealType('dinner');
        } else {
          setSelectedMealType('lunch'); // Default to lunch if both available
        }
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
      setTodayMenu({ hasMenu: false, message: 'Error loading menu' });
    }
    setMenuLoading(false);
  };

  // Handle external scanner input
  const handleExternalScan = (value) => {
    if (!value.trim()) return;
    
    const qrData = value.trim();
    
    // Check for duplicates
    const isDuplicate = scannedItems.some(item => item.qr_data === qrData);
    
    if (isDuplicate) {
      showMessage('warning', `QR code "${qrData}" already scanned!`);
      return;
    }
    
    // Add to scanned items
    const newItem = {
      id: uuidv4(),
      qr_data: qrData,
      timestamp: new Date().toISOString(),
      method: 'external'
    };
    
    setScannedItems(prev => [...prev, newItem]);
    showMessage('success', `QR code "${qrData}" added successfully!`);
  };

  // Handle manual input
  const handleManualAdd = () => {
    const input = scannerInputRef.current.value.trim();
    if (input) {
      handleExternalScan(input);
      scannerInputRef.current.value = '';
    }
  };

  // Handle successful QR scan from camera with cooldown
  const handleCameraScan = (qrData) => {
    // Prevent rapid scanning during cooldown
    if (scanCooldown) {
      console.log('Scan cooldown active, ignoring scan');
      return;
    }
    
    // Check for duplicates in the list
    const isDuplicate = scannedItems.some(item => item.qr_data === qrData);
    
    if (isDuplicate) {
      showMessage('warning', `QR code "${qrData}" already scanned!`);
      // Set shorter cooldown for duplicates
      setScanCooldown(true);
      setTimeout(() => setScanCooldown(false), 1000);
      return;
    }
    
    // Add to scanned items
    const newItem = {
      id: uuidv4(),
      qr_data: qrData,
      timestamp: new Date().toISOString(),
      method: 'camera'
    };
    
    setScannedItems(prev => [...prev, newItem]);
    setLastScannedQR(qrData);
    showMessage('success', `QR code "${qrData}" scanned successfully!`);
    
    // Set cooldown to prevent rapid scanning
    setScanCooldown(true);
    setTimeout(() => {
      setScanCooldown(false);
      setLastScannedQR('');
    }, 2000); // 2 second cooldown
  };

  // Handle camera tap for manual QR simulation
  const handleCameraClick = () => {
    if (isCameraActive) {
      const testQR = prompt('Simulate QR scan - Enter QR data:');
      if (testQR && testQR.trim()) {
        handleCameraScan(testQR.trim());
      }
    }
  };

  // Start camera with proper initialization
  const startCamera = async () => {
    console.log('Starting camera...');
    
    if (scannerInputRef.current) {
      scannerInputRef.current.blur();
    }
    
    try {
      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Camera stream obtained');
      
      setIsCameraActive(true);
      
      // Wait for video element to be rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          videoRef.current.onloadedmetadata = () => {
            console.log('Video metadata loaded');
            
            videoRef.current.play().then(() => {
              console.log('Video playing successfully');
              
              // Wait for video to stabilize, then start scanning
              setTimeout(() => {
                startScanning();
              }, 1000);
              
            }).catch(err => {
              console.error('Video play failed:', err);
              showMessage('error', 'Failed to start video playback');
            });
          };
          
        } else {
          console.error('Video ref not available');
          showMessage('error', 'Video element not ready');
          setIsCameraActive(false);
          stream.getTracks().forEach(track => track.stop());
        }
      }, 200);
      
    } catch (error) {
      console.error('Camera error:', error);
      showMessage('error', `Camera error: ${error.message}`);
      setIsCameraActive(false);
    }
  };

  // Stop camera and cleanup
  const stopCamera = () => {
    console.log('Stopping camera...');
    
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    
    setIsCameraActive(false);
    setScanCooldown(false);
    setLastScannedQR('');
    
    if (scanningIntervalRef.current) {
      clearInterval(scanningIntervalRef.current);
      scanningIntervalRef.current = null;
    }
    
    setTimeout(() => {
      if (scannerInputRef.current) {
        scannerInputRef.current.focus();
      }
    }, 100);
  };

  // QR Code detection with proper canvas handling
  const startScanning = () => {
    if (scanningIntervalRef.current) {
      clearInterval(scanningIntervalRef.current);
      scanningIntervalRef.current = null;
    }
    
    console.log('Starting QR code scanning...');
    
    let scanCount = 0;
    
    scanningIntervalRef.current = setInterval(() => {
      scanCount++;
      
      const videoReady = videoRef.current && videoRef.current.readyState === 4;
      const refsExist = videoRef.current && canvasRef.current;
      const hasVideoSource = videoRef.current && videoRef.current.srcObject;
      const cameraIsActive = isCameraActive || hasVideoSource;
      
      if (refsExist && cameraIsActive && videoReady) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        
        // Set canvas size to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        if (canvas.width > 0 && canvas.height > 0) {
          // Draw current video frame to canvas
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          try {
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            
            // Detect QR code
            const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: "dontInvert",
            });
            
            if (qrCode && !scanCooldown) {
              console.log('QR Code detected via auto-scan:', qrCode.data);
              handleCameraScan(qrCode.data);
            }
            
          } catch (error) {
            console.error('QR detection error:', error);
          }
        }
      }
    }, 300);
  };

  // Remove scanned item
  const removeScannedItem = (id) => {
    setScannedItems(prev => prev.filter(item => item.id !== id));
    showMessage('info', 'Item removed');
  };

  // Clear all scanned items
  const clearAllItems = () => {
    setScannedItems([]);
    setLastScannedQR('');
    setScanCooldown(false);
    showMessage('info', 'All items cleared');
  };

  // Submit all scanned data to backend
  const submitAllScans = async () => {
    if (scannedItems.length === 0) {
      showMessage('warning', 'No items to submit');
      return;
    }

    if (!selectedMealType) {
      showMessage('warning', 'Please select meal type (lunch/dinner)');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        scans: scannedItems.map(item => ({
          qr_data: item.qr_data,
        })),
        mealType: selectedMealType,
        scanDate: new Date().toISOString().split('T')[0]
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/batch-scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        showMessage('success', `Successfully submitted ${result.processed}/${result.total} ${selectedMealType} scans!`);
        setScannedItems([]);
        setLastScannedQR('');
        setScanCooldown(false);
      } else {
        throw new Error('Failed to submit scans');
      }
      
    } catch (error) {
      showMessage('error', 'Failed to submit scans. Please try again.');
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show message helper
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  // Format timestamp for display
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  // Render meal selection section
  const renderMealSelection = () => {
    if (menuLoading) {
      return (
        <div className="meal-selection loading">
          <div className="loading-spinner">Loading menu...</div>
        </div>
      );
    }

    if (!todayMenu?.hasMenu) {
      return (
        <div className="meal-selection no-menu">
          <div className="warning-icon">⚠️</div>
          <p>No menu available for today</p>
          <p>{todayMenu?.message}</p>
        </div>
      );
    }

    return (
      <div className="meal-selection">
        <h3>Select Meal Type</h3>
        <div className="meal-options">
          {todayMenu.lunch.isActive && (
            <div 
              className={`meal-option ${selectedMealType === 'lunch' ? 'selected' : ''}`}
              onClick={() => setSelectedMealType('lunch')}
            >
              <div className="meal-header">
                <input 
                  type="radio" 
                  name="mealType" 
                  value="lunch" 
                  checked={selectedMealType === 'lunch'}
                  onChange={() => setSelectedMealType('lunch')}
                />
                <label>🌅 Lunch</label>
              </div>
              <div className="meal-items">
                {todayMenu.lunch.items.length > 0 ? (
                  todayMenu.lunch.items.map((item, i) => (
                    <span key={i} className="menu-item">{item}</span>
                  ))
                ) : (
                  <span className="no-items">No items</span>
                )}
              </div>
            </div>
          )}

          {todayMenu.dinner.isActive && (
            <div 
              className={`meal-option ${selectedMealType === 'dinner' ? 'selected' : ''}`}
              onClick={() => setSelectedMealType('dinner')}
            >
              <div className="meal-header">
                <input 
                  type="radio" 
                  name="mealType" 
                  value="dinner" 
                  checked={selectedMealType === 'dinner'}
                  onChange={() => setSelectedMealType('dinner')}
                />
                <label>🌙 Dinner</label>
              </div>
              <div className="meal-items">
                {todayMenu.dinner.items.length > 0 ? (
                  todayMenu.dinner.items.map((item, i) => (
                    <span key={i} className="menu-item">{item}</span>
                  ))
                ) : (
                  <span className="no-items">No items</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Show no thaali messages */}
        {(!todayMenu.lunch.isActive && todayMenu.lunch.comment) && (
          <div className="no-thaali-message">
            <strong>Lunch:</strong> No Thaali - {todayMenu.lunch.comment}
          </div>
        )}
        
        {(!todayMenu.dinner.isActive && todayMenu.dinner.comment) && (
          <div className="no-thaali-message">
            <strong>Dinner:</strong> No Thaali - {todayMenu.dinner.comment}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="qr-scanner-container">
      <div className="scanner-header">
        <h2>Tiffin QR Scanner</h2>
        <div className="scanner-stats">
          <span>Items Scanned: {scannedItems.length}</span>
          <span>Meal: {selectedMealType}</span>
        </div>
      </div>

      {/* Meal Selection */}
      {renderMealSelection()}

      {/* Status Info */}
      <div className="status-info">
        <div className="status-item">
          <span className="status-label">Camera:</span>
          <span className={`status-value ${isCameraActive ? 'active' : 'inactive'}`}>
            {isCameraActive ? '✅ Active' : '❌ Inactive'}
          </span>
        </div>
        <div className="status-item">
          <span className="status-label">Scanning:</span>
          <span className={`status-value ${scanningIntervalRef.current ? 'active' : 'inactive'}`}>
            {scanningIntervalRef.current ? '🔍 Running' : '⏸️ Stopped'}
          </span>
        </div>
        {scanCooldown && (
          <div className="status-item">
            <span className="status-label">Cooldown:</span>
            <span className="status-value cooldown">⏱️ Active</span>
          </div>
        )}
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Scanner Controls */}
      <div className="scanner-controls">
        <button 
          className="camera-btn"
          onClick={isCameraActive ? stopCamera : startCamera}
          type="button"
        >
          {isCameraActive ? '📷 Stop Camera' : '📷 Start Camera'}
        </button>
        
        <button 
          className="clear-btn"
          onClick={clearAllItems}
          disabled={scannedItems.length === 0}
        >
          🗑️ Clear All
        </button>
      </div>

      {/* Camera View */}
      {isCameraActive && (
        <div className="camera-container">
          <div className="camera-wrapper">
            <video 
              ref={videoRef} 
              className="camera-video"
              onClick={handleCameraClick}
              playsInline
              muted
              autoPlay
            />
            
            <div className="camera-overlay">
              <div className="scan-area">
                <div className="scan-corners">
                  <div className="corner corner-tl"></div>
                  <div className="corner corner-tr"></div>
                  <div className="corner corner-bl"></div>
                  <div className="corner corner-br"></div>
                </div>
                <div className="scan-instructions">
                  {scanCooldown ? 'Processing...' : 'Position QR code here'}
                </div>
              </div>
            </div>
          </div>
          
          {/* Hidden canvas for QR processing */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}

      {/* External Scanner Input */}
      <div className="external-scanner">
        <h3>External Scanner / Manual Input</h3>
        <div className="input-group">
          <input
            ref={scannerInputRef}
            id="scanner-input"
            type="text"
            placeholder="Focus here for external scanner or type manually"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleExternalScan(e.target.value);
                e.target.value = '';
              }
            }}
            onBlur={() => {
              if (!isCameraActive) {
                setTimeout(() => {
                  if (scannerInputRef.current) {
                    scannerInputRef.current.focus();
                  }
                }, 100);
              }
            }}
          />
          <button onClick={handleManualAdd} className="add-btn">
            ➕ Add
          </button>
        </div>
        <p className="scanner-help">
          📱 External scanners will automatically add items when you scan<br/>
          ⌨️ Or type QR data manually and press Enter
        </p>
      </div>

      {/* Scanned Items List */}
      <div className="scanned-items">
        <h3>Scanned Items ({scannedItems.length}) - {selectedMealType}</h3>
        
        {scannedItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <p>No items scanned yet</p>
            <p>Use camera scanner or external scanner to add items</p>
          </div>
        ) : (
          <div className="items-list">
            {scannedItems.map((item) => (
              <div key={item.id} className="scanned-item">
                <div className="item-info">
                  <span className="qr-data">{item.qr_data}</span>
                  <div className="item-meta">
                    <span className="timestamp">{formatTime(item.timestamp)}</span>
                    <span className={`method ${item.method}`}>
                      {item.method === 'camera' ? '📷 Camera' : '🔍 Scanner'}
                    </span>
                  </div>
                </div>
                <button 
                  className="remove-btn"
                  onClick={() => removeScannedItem(item.id)}
                  title="Remove item"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="submit-section">
        <button 
          className="submit-btn"
          onClick={submitAllScans}
          disabled={scannedItems.length === 0 || isSubmitting || !selectedMealType}
        >
          {isSubmitting ? '⏳ Submitting...' : `📤 Submit ${selectedMealType} Scans (${scannedItems.length} items)`}
        </button>
      </div>
    </div>
  );
};

export default QRScanner;